// UserGuide — in-app walkthrough for creating & approving work orders.
// No API calls; static content only. Accessible to any authenticated user.
import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, FileText, CheckCircle, XCircle, Mail, Users,
  Palette, GitBranch, UserCheck, Shield, Upload, PlusCircle, Send, ArrowRight, HelpCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import OnboardingSlideshow from '../components/OnboardingSlideshow';

// Small primitives — inline styles keep this page self-contained.
const Step = ({ num, title, children }) => (
  <div style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'flex-start' }}>
    <div style={{
      minWidth: 32, height: 32, borderRadius: '50%', background: 'var(--navy)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14,
    }}>{num}</div>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--heading)', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#4a5568', lineHeight: 1.6 }}>{children}</div>
    </div>
  </div>
);

const Section = ({ icon, title, subtitle, children, id }) => (
  <div id={id} className="form-card" style={{ marginBottom: 24, scrollMarginTop: 24 }}>
    <div style={{ padding: 20, borderBottom: '1px solid #eef1f5', display: 'flex', gap: 12, alignItems: 'center' }}>
      <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--gold-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {React.cloneElement(icon, { className: 'w-5 h-5', style: { color: 'var(--gold)' } })}
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 16, color: 'var(--heading)' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{subtitle}</div>}
      </div>
    </div>
    <div style={{ padding: 20 }}>{children}</div>
  </div>
);

const StatusPill = ({ label, color, bg }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
    letterSpacing: '.3px', color, background: bg, textTransform: 'uppercase', margin: '0 4px',
  }}>{label}</span>
);

const UserGuide = () => {
  const { user } = useAuth();
  const { branding } = useBranding();
  const isAdmin = Array.isArray(user?.permissions) && user.permissions.includes('users.manage');

  return (
    <div className="animate-fadeIn" data-testid="user-guide-page" style={{ paddingBottom: 40 }}>
      <div className="page-header">
        <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BookOpen className="w-6 h-6" style={{ color: 'var(--heading)' }} />
          User Guide
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Everything you need to raise a Work Order and shepherd it through approval on {branding.app_name || 'this app'}.
        </div>
      </div>

      {/* AI-narrated walkthrough */}
      <OnboardingSlideshow />

      {/* Quick-jump TOC */}
      <div className="form-card" style={{ marginBottom: 24, padding: 16, background: 'var(--gold-pale)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.8px', color: 'var(--heading)', marginBottom: 8 }}>QUICK JUMP</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            ['#overview', 'Overview', HelpCircle],
            ['#create', 'Create a Work Order', PlusCircle],
            ['#lifecycle', 'Status Lifecycle', ArrowRight],
            ['#approve', 'Approve / Reject', CheckCircle],
            ['#magic', 'Approve via Email', Mail],
            ['#delegate', 'Delegation', UserCheck],
            ...(isAdmin ? [
              ['#matrix', 'Approval Matrix', GitBranch],
              ['#users', 'User Management', Shield],
              ['#branding', 'Branding', Palette],
            ] : []),
          ].map(([href, label, Icon]) => (
            <a key={href} href={href} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
              background: '#fff', borderRadius: 20, fontSize: 12, color: 'var(--navy)',
              textDecoration: 'none', fontWeight: 500, border: '1px solid #e5e7eb',
            }}>
              <Icon className="w-3 h-3" />{label}
            </a>
          ))}
        </div>
      </div>

      {/* Overview */}
      <Section id="overview" icon={<HelpCircle />} title="Overview" subtitle="What this app does">
        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, margin: 0 }}>
          This is your central register for <strong>Work Orders</strong> — housekeeping, AMC (Annual Maintenance),
          security, F&amp;B, and any other unified service agreement. A Work Order moves through a
          predictable lifecycle: <strong>Draft → Pending Finance → Active</strong>. Finance can approve or
          reject either inside the app or straight from their email inbox via a one-click magic link.
        </p>
      </Section>

      {/* Create */}
      <Section id="create" icon={<PlusCircle />} title="Creating a Work Order" subtitle="From blank to submitted in six steps">
        <Step num={1} title="Start a new draft">
          Click <strong>New Contract</strong> in the left sidebar (or the <strong>New Work Order</strong>
          card on the Dashboard). A fresh Work Order number is reserved for you the moment the form opens.
        </Step>
        <Step num={2} title="Fill Contract Identity">
          Pick the <strong>Agreement Date</strong>, <strong>AMC Type</strong> (Housekeeping / F&amp;B /
          Security / etc.) and if this is a renewal, tick the box and add the previous WO number.
        </Step>
        <Step num={3} title="Add the Service Provider">
          Type the vendor's legal name and choose <em>Private Limited</em>, <em>Sole Proprietorship</em>, etc.
          When you paste a <strong>GSTIN</strong>, the PAN auto-fills. Add the authorised signatory,
          designation, address, phone and email.
        </Step>
        <Step num={4} title="Set Period &amp; Value">
          Pick <strong>Start</strong> and <strong>End</strong> dates, the <strong>Monthly Value</strong>,
          <strong>GST rate</strong>, and billing frequency (monthly / quarterly / annual).
          The dashboard totals compute automatically.
        </Step>
        <Step num={5} title="Scope of Work &amp; Rates (optional)">
          Add a plain-text scope, plus a <strong>Schedule of Rates</strong> for manpower / consumables /
          other charges. You can also upload a full Excel annexure — it will render natively inside the PDF
          &amp; Word exports.
        </Step>
        <Step num={6} title="Save &amp; Submit">
          <strong>Save Draft</strong> to park it, or hit <strong>Submit for Finance Approval</strong>.
          Submission fires an email to the right approver based on your Approval Matrix.
        </Step>
      </Section>

      {/* Lifecycle */}
      <Section id="lifecycle" icon={<ArrowRight />} title="Status Lifecycle" subtitle="How a Work Order moves">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
          <StatusPill label="Draft" color="#92400e" bg="#fef3c7" />
          <ArrowRight className="w-4 h-4" style={{ color: '#999' }} />
          <StatusPill label="Pending Finance" color="#1e40af" bg="#dbeafe" />
          <ArrowRight className="w-4 h-4" style={{ color: '#999' }} />
          <StatusPill label="Active" color="#065f46" bg="#d1fae5" />
        </div>
        <ul style={{ marginTop: 16, fontSize: 13, color: '#374151', lineHeight: 1.8, paddingLeft: 20 }}>
          <li><strong>Draft</strong> — editable by procurement / creator. Not yet visible to Finance.</li>
          <li><strong>Pending Finance</strong> — locked for procurement. Finance sees it in their queue + gets an email.</li>
          <li><strong>Active</strong> — approved. Edits require a written <em>reason for change</em> and produce an amendment log entry.</li>
          <li><strong>Rejected</strong> — returned to Draft with the rejection note attached.</li>
        </ul>
      </Section>

      {/* Approve */}
      <Section id="approve" icon={<CheckCircle />} title="Approving or Rejecting" subtitle="Two ways in — inside the app or from your inbox">
        <Step num={1} title="Open the Work Order">
          Finance users see a <em>Pending Finance</em> filter on the Work Orders list. Click any row to open.
        </Step>
        <Step num={2} title="Review the details">
          Verify vendor, period, monthly value, GST, scope, and annexures. Download the PDF/Word if needed.
        </Step>
        <Step num={3} title="Approve or Reject">
          Hit the <strong style={{ color: '#065f46' }}>Approve</strong> button (moves to Active) or
          <strong style={{ color: '#991b1b' }}> Reject</strong> (returns to Draft with your note).
          Both actions are logged with your name and timestamp.
        </Step>
      </Section>

      {/* Magic link */}
      <Section id="magic" icon={<Mail />} title="Approving via Email (Magic Link)" subtitle="Zero clicks — no login needed">
        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, margin: '0 0 12px' }}>
          When a Work Order is submitted, the finance approver receives an email with two buttons —
          <strong> Approve</strong> and <strong>Reject</strong>. Each is a signed one-time link valid
          for 3 days. Clicking it lands on a public confirmation page; no login required. Once used,
          the link is invalidated. Perfect for approvals on mobile without hunting for the password.
        </p>
      </Section>

      {/* Delegation */}
      <Section id="delegate" icon={<UserCheck />} title="Out-of-Office Delegation" subtitle="Never bottleneck an approval">
        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, margin: '0 0 12px' }}>
          Going on leave? Open <Link to="/my-delegation" style={{ color: 'var(--navy)', fontWeight: 500 }}>My Delegation</Link>,
          pick a delegate, set a <strong>from</strong> and <strong>to</strong> date, and any pending approvals
          matching your role during that window will be routed to the delegate automatically — email
          included. Clear the delegation any time.
        </p>
      </Section>

      {/* Admin-only sections */}
      {isAdmin && (
        <>
          <Section id="matrix" icon={<GitBranch />} title="Approval Matrix (Admin)" subtitle="Route approvals by amount, department, or type">
            <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, margin: '0 0 12px' }}>
              Open <Link to="/approval-matrix" style={{ color: 'var(--navy)', fontWeight: 500 }}>Approval Matrix</Link>.
              Create rules like <em>"Below ₹1L → Finance Team"</em> or <em>"Above ₹10L → Finance Manager + Super Admin"</em>.
              Rules are matched by <strong>priority</strong> (lowest number wins), amount range, and optional
              department / AMC type. If no rule matches, the fallback is <em>all finance roles + Super Admin</em>.
            </p>
          </Section>

          <Section id="users" icon={<Shield />} title="User Management (Admin)" subtitle="Add teammates and set roles">
            <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, margin: '0 0 12px' }}>
              Open <Link to="/users" style={{ color: 'var(--navy)', fontWeight: 500 }}>User Management</Link>.
              Create users with an email, name, temp password, and a role: <strong>Super Admin</strong>,
              <strong> User Admin</strong>, <strong>Procurement Team</strong>, <strong>Finance Team</strong>,
              or <strong>Finance Manager</strong>. Users can change their own password after first login.
              Soft-delete (deactivate) any user any time.
            </p>
          </Section>

          <Section id="branding" icon={<Palette />} title="Branding (Admin)" subtitle="Make it yours in a minute">
            <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, margin: '0 0 12px' }}>
              Open <Link to="/branding" style={{ color: 'var(--navy)', fontWeight: 500 }}>Branding</Link>.
              Upload your <strong>logo</strong> (crop &amp; reposition inline), pick your
              <strong> primary</strong> and <strong>accent</strong> colours, adjust <strong>sidebar text</strong>
              and <strong>page heading</strong> colours, and control <strong>logo height</strong> plus
              <strong> fit</strong>. Live Preview updates as you tweak.
            </p>
          </Section>
        </>
      )}

      <div className="form-card" style={{ padding: 24, textAlign: 'center', background: 'var(--gold-pale)' }}>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 600, color: 'var(--heading)' }}>
          Ready to raise your first Work Order?
        </div>
        <div style={{ fontSize: 13, color: '#4a5568', margin: '6px 0 14px' }}>
          Start with a blank draft — you can save and come back any time.
        </div>
        <Link to="/create" data-testid="guide-cta-create" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'var(--navy)', color: '#fff', padding: '10px 22px', borderRadius: 8,
          fontSize: 14, fontWeight: 500, textDecoration: 'none',
        }}>
          <PlusCircle className="w-4 h-4" /> Create Work Order
        </Link>
      </div>
    </div>
  );
};

export default UserGuide;
